'use client';

import type { Row, Table } from '@tanstack/react-table';

import { Button } from '#/components/ui/button';
import { Input } from '#/components/ui/input';

import {
  DataTableFacetedFilter,
  type FilterOption
} from './data-table-faceted-filter';
import { DataTableViewOptions } from './data-table-view-options';
import { useAlertModal } from '#/hooks/use-alert-modal';
import { toast } from 'react-toastify';
import { ToolbarMoreActions } from './toolbar-more-actions';
import { cn } from '#/lib/utils';
import { DatePickerWithRange } from './date-range-picker';
import { DateFormat } from '#/lib/date';
import { usePathname, useRouter } from 'next/navigation';
import { OptionTreeItemType } from 'types/common';
import { IconType, Icons } from '../icons';
import { DataTableTreeFacetedFilter } from './data-table-tree-faceted-filter';

interface ButtonVariant {
  variant?:
    | 'link'
    | 'default'
    | 'destructive'
    | 'outline'
    | 'secondary'
    | 'ghost'
    | null
    | undefined;
}

export interface DataTableHeaderAction<TData> extends ButtonVariant {
  text: string;
  onClick: (selected: Row<TData>[], table: Table<TData>) => void;
  Icon: IconType;
}

export interface DataTableDeleteAction extends ButtonVariant {
  title?: (selecctedCnt: number) => string;
  description?: string;
  buttonText?: string;
}

interface DataTableToolbarProps<TData> {
  table: Table<TData>;
  filterableColumns?: {
    id: string;
    title: string;
    options: FilterOption[] | OptionTreeItemType[];
    tree?: boolean;
  }[];
  searchableColumns?: {
    id: string;
    title: string;
    className?: string;
  }[];
  rangableColumns?: {
    id: string;
    title: string;
    className?: string;
  }[];
  onDelete?: (rows: TData[], table: Table<TData>) => Promise<void>;
  onDeleteValidate?: (rows: TData[], table: Table<TData>) => boolean;
  actions?: DataTableHeaderAction<TData>[];
  remote?: boolean;
  deleteAction?: DataTableDeleteAction;
  isNotRemovePram?: URLSearchParams;
  isViewOption?: boolean;
}

export function DataTableToolbar<TData>({
  table,
  onDelete,
  onDeleteValidate,
  remote,
  deleteAction,
  filterableColumns = [],
  searchableColumns = [],
  rangableColumns = [],
  actions = [],
  isNotRemovePram,
  isViewOption = true
}: DataTableToolbarProps<TData>) {
  const pathname = usePathname();
  const { openDelete, close } = useAlertModal();
  const isFiltered = table.getState().columnFilters.length > 0;
  const selectedRows = table.getFilteredSelectedRowModel().rows;
  const selected = selectedRows.length;
  const router = useRouter();

  return (
    <div className="flex items-start justify-between">
      <div
        className={cn(
          'grid grid-rows-1 grid-flow-col gap-2',
          filterableColumns.length > 5 ? 'sm:grid-rows-2' : ''
        )}
      >
        <div className="flex items-center space-x-2">
          {rangableColumns.length > 0 &&
            rangableColumns.map((column) => {
              const val =
                (table
                  .getColumn(String(column.id))
                  ?.getFilterValue() as string) ?? '';
              let from = undefined;
              let to = undefined;
              const fromTo = val.split('|');
              if (fromTo.length === 2) {
                try {
                  from = new Date(fromTo[0]);
                  to = new Date(fromTo[1]);
                } catch (error) {}
              }
              return (
                table.getColumn(column.id ? String(column.id) : '') && (
                  <DatePickerWithRange
                    key={String(column.id)}
                    title={column.title}
                    from={from}
                    to={to}
                    onSelect={(range) => {
                      if (range && range.from && range.to) {
                        table
                          .getColumn(String(column.id))
                          ?.setFilterValue(
                            `${DateFormat(range.from)}|${DateFormat(range.to)}`
                          );
                      }
                    }}
                    className={cn(
                      'h-8 w-[150px] lg:w-[250px]',
                      column.className
                    )}
                  />
                )
              );
            })}
          {searchableColumns.length > 0 &&
            searchableColumns.map(
              (column) =>
                table.getColumn(column.id ? String(column.id) : '') && (
                  <Input
                    key={String(column.id)}
                    placeholder={`${column.title}`}
                    value={
                      (table
                        .getColumn(String(column.id))
                        ?.getFilterValue() as string) ?? ''
                    }
                    onChange={(event) =>
                      table
                        .getColumn(String(column.id))
                        ?.setFilterValue(event.target.value)
                    }
                    className={cn(
                      'h-8 w-[130px] lg:w-[250px]',
                      column.className
                    )}
                  />
                )
            )}
        </div>
        <div className="hidden sm:flex items-center space-x-2">
          {filterableColumns.length > 0 &&
            filterableColumns.map((column) => {
              const tableColumn = table.getColumn(
                column.id ? String(column.id) : ''
              );
              if (tableColumn) {
                if (column.tree) {
                  return (
                    <DataTableTreeFacetedFilter
                      key={String(column.id) + 'tree'}
                      column={table.getColumn(
                        column.id ? String(column.id) : ''
                      )}
                      title={column.title}
                      options={column.options as OptionTreeItemType[]}
                    />
                  );
                } else {
                  return (
                    <DataTableFacetedFilter
                      key={String(column.id)}
                      column={table.getColumn(
                        column.id ? String(column.id) : ''
                      )}
                      title={column.title}
                      options={column.options as FilterOption[]}
                      remote={remote}
                    />
                  );
                }
              }
              return null;
            })}
          {isFiltered && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                if (remote) {
                  const searchParams = new URLSearchParams({ page: '1' });
                  // window.location.href = `${pathname}?${searchParams}&${isNotRemovePram}`;
                  router.push(
                    `${pathname}?${searchParams}${isNotRemovePram ? `&${isNotRemovePram}` : ''}`
                  );
                  table.resetColumnFilters();
                } else {
                  table.resetColumnFilters();
                }
              }}
              className="h-8 px-2 lg:px-3"
            >
              초기화
              <Icons.close className="ml-2 h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
      <div className="flex items-center space-x-2">
        <div className="hidden h-8 lg:flex ml-2">
          {actions.map((a) => {
            return (
              <Button
                type="button"
                key={a.text}
                disabled={!selected}
                onClick={() => a.onClick(selectedRows, table)}
                variant={a.variant && selected > 0 ? a.variant : 'outline'}
                className="h-8 px-2 lg:px-3 mr-2"
              >
                <a.Icon className="mr-2 h-4 w-4" />
                {a.text}
                {selected !== 0 && `(${selected})`}
              </Button>
            );
          })}
        </div>
        {actions.length > 0 && (
          <ToolbarMoreActions
            className="hidden h-8 max-lg:flex ml-2"
            actions={actions.map((a) => ({
              title: a.text,
              disabled: !selected,
              onClick: () => a.onClick(selectedRows, table)
            }))}
          />
        )}
        {onDelete && (
          <Button
            type="button"
            variant={
              selected
                ? deleteAction?.variant
                  ? deleteAction.variant
                  : 'destructive'
                : 'outline'
            }
            disabled={!selected}
            onClick={() => {
              if (
                onDeleteValidate &&
                onDeleteValidate(
                  selectedRows.map((r) => r.original),
                  table
                ) !== true
              ) {
                return;
              }
              openDelete({
                title: deleteAction?.title
                  ? deleteAction.title(selected)
                  : `${selected}건의 데이터를 삭제하시겠습니까?`,
                description: deleteAction?.description,
                onConfirm: async () => {
                  await onDelete(
                    selectedRows.map((r) => r.original),
                    table
                  );
                  table.resetRowSelection();
                  toast.success(`${selected}건의 데이터가 삭제되었습니다.`);
                  close();
                }
              });
            }}
            className="h-8 px-2 lg:px-3"
          >
            <Icons.trash className="mr-2 h-4 w-4" />
            {deleteAction?.buttonText ?? '삭제'}
            {selected !== 0 && `(${selected})`}
          </Button>
        )}
        {isViewOption && <DataTableViewOptions table={table} remote={remote} />}
      </div>
    </div>
  );
}
